import { useState, useCallback } from 'react';
import {
  emptyAddress,
  ensureOnePrimary,
  removeAddressAt,
  toSavePayload,
  withPrimaryAt,
  type CustomerAddress,
} from '@shared/components';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser, getUser, logout } from '../state/authSlice';
import { StoreLocationApi, CourierUserApi } from '@shared/sdk-v1';
import { useEffect } from 'react';
import { apiConfig } from '../shared/api-config';
import { getCourier } from '@state/courierSlice';
import { useTrackEvent } from '../utils/track';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { toE164 } from '../utils/phone';
import { effectiveDeliveryPreference, DeliveryPreference } from '../utils/deliveryPreference';

export const useProfile = () => {
  const dispatch = useDispatch();
  const track = useTrackEvent();
  const user = useSelector(getUser);
  const courier = useSelector(getCourier);
  // console.log('user', user);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [storeLocations, setStoreLocations] = useState<Array<{value: string, label: string}>>([
    { value: '', label: 'Select a store location' }
  ]);

  // Password change state
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Delete account state
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  // Fetch store locations
  useEffect(() => {
    const fetchStoreLocations = async () => {
      try {
       
        const storeLocationApi = new StoreLocationApi(apiConfig());
        const locations = await storeLocationApi.storeLocationFind({
          filter: JSON.stringify({
            where: {
              courierId: courier?.id?.toString()
            }
          })
        });
        
        const formattedLocations = locations.map(location => ({
          value: location.id?.toString() || '',
          label: location.name || location.id?.toString() || 'Unknown Location'
        }));

        setStoreLocations([
          { value: '', label: 'Select a store location' },
          ...formattedLocations
        ]);
      } catch (err) {
        console.error('Error fetching store locations:', err);
        setError('Failed to load store locations');
      }
    };

    fetchStoreLocations();
  }, []);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    storeLocationId: '',
    // The full address book; exactly one entry carries isPrimary.
    addresses: [{ ...emptyAddress(), isPrimary: true }] as CustomerAddress[],
    // Additional profile fields
    gender: '',
    dateOfBirth: '',
    trnNumber: '',
    // '' = the customer has not chosen yet (the stored 'delivery' is a default)
    deliveryPreference: '' as DeliveryPreference | ''
  });

      // Initialize form data from user
  useEffect(() => {
    if (user) {
      
      // Normalize dateOfBirth to YYYY-MM-DD format for the date input
      let normalizedDateOfBirth = '';
      if (user.dateOfBirth) {
        try {
          // If it's already in YYYY-MM-DD format, use it as is
          // Otherwise, parse it as a Date and extract the date part
          const dateStr = user.dateOfBirth;
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            normalizedDateOfBirth = dateStr;
          } else {
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
              normalizedDateOfBirth = date.toISOString().split('T')[0];
            }
          }
        } catch (e) {
          console.error('Error parsing dateOfBirth:', e);
        }
      }
      
      setFormData({
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email ?? '',
        phone: toE164(user.contactInformation?.mobilePhone ?? ''),
        storeLocationId: user.storeLocationId?.toString() ?? '',
        addresses: ensureOnePrimary(user.addresses ?? []),
        // Additional profile fields
        gender: user.gender ?? '',
        dateOfBirth: normalizedDateOfBirth,
        trnNumber: user.trnNumber ?? '',
        deliveryPreference: effectiveDeliveryPreference(user) ?? ''
      });
    }
  }, [user]);

  const handleFieldChange = useCallback((data: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    storeLocationId?: number;
    // Additional profile fields
    gender?: string;
    dateOfBirth?: string;
    trnNumber?: string;
    deliveryPreference?: DeliveryPreference;
  }) => {
    // Update form state only
    setFormData(prev => ({
      ...prev,
      firstName: data.firstName !== undefined ? data.firstName : prev.firstName,
      lastName: data.lastName !== undefined ? data.lastName : prev.lastName,
      email: data.email !== undefined ? data.email : prev.email,
      phone: data.phone !== undefined ? data.phone : prev.phone,
      storeLocationId: data.storeLocationId !== undefined ? data.storeLocationId.toString() : prev.storeLocationId,
      // Additional profile fields
      gender: data.gender !== undefined ? data.gender : prev.gender,
      dateOfBirth: data.dateOfBirth !== undefined ? data.dateOfBirth : prev.dateOfBirth,
      trnNumber: data.trnNumber !== undefined ? data.trnNumber : prev.trnNumber,
      deliveryPreference: data.deliveryPreference !== undefined ? data.deliveryPreference : prev.deliveryPreference
    }));
  }, []);

  // Addresses are an array, so they get indexed handlers of their own rather
  // than going through handleFieldChange's flat merge.
  const handleAddressChange = useCallback(
    (index: number, field: keyof CustomerAddress, value: unknown) => {
      setFormData(prev => ({
        ...prev,
        addresses: prev.addresses.map((address, i) =>
          i === index ? { ...address, [field]: value } : address
        ),
      }));
    },
    []
  );

  const handleAddAddress = useCallback(() => {
    setFormData(prev => ({ ...prev, addresses: [...prev.addresses, emptyAddress()] }));
  }, []);

  const handleRemoveAddress = useCallback((index: number) => {
    setFormData(prev => ({ ...prev, addresses: removeAddressAt(prev.addresses, index) }));
  }, []);

  const handleSetPrimaryAddress = useCallback((index: number) => {
    setFormData(prev => ({ ...prev, addresses: withPrimaryAt(prev.addresses, index) }));
  }, []);

  const saveProfile = useCallback(
    async (
      options?: {
        restrictTo?: 'email' | 'phone';
        override?: Partial<{ email: string; phone: string }>;
        force?: boolean;
      }
    ) => {
    // Only include fields that have actually changed
    const changes: any = {};
    let hasChanges = false;
    
    // Trim strings for comparison to handle empty strings vs null/undefined
    const trimmedFirstName = formData.firstName.trim();
    const trimmedLastName = formData.lastName.trim();
    const trimmedEmail = (options?.override?.email ?? formData.email).trim();
    const trimmedPhone = (options?.override?.phone ?? formData.phone).trim();
    
    if (trimmedFirstName !== (user?.firstName || '').trim()) {
      changes.firstName = trimmedFirstName;
      hasChanges = true;
      // console.log('First name changed:', { old: user?.firstName, new: trimmedFirstName });
    }
    
    if (trimmedLastName !== (user?.lastName || '').trim()) {
      changes.lastName = trimmedLastName;
      hasChanges = true;
      // console.log('Last name changed:', { old: user?.lastName, new: trimmedLastName });
    }
    
    const shouldIncludeEmail = !options?.restrictTo || options.restrictTo === 'email';
    if (shouldIncludeEmail) {
      if (options?.force || trimmedEmail !== (user?.email || '').trim()) {
        changes.email = trimmedEmail;
        hasChanges = true;
        // console.log('Email changed:', { old: user?.email, new: trimmedEmail });
      }
    }
    
    // Phone validation: accept any valid international number (E.164).
    // The phone InputField emits canonical E.164; compare the normalized forms so
    // formatting or legacy (non-"+") differences don't cause a false "changed".
    const normalizedInputPhone = toE164(trimmedPhone);
    const normalizedExistingPhone = toE164(user?.contactInformation?.mobilePhone || '');
    const shouldIncludePhone = !options?.restrictTo || options.restrictTo === 'phone';

    if (shouldIncludePhone) {
      if (options?.force || normalizedInputPhone !== normalizedExistingPhone) {
        if (!normalizedInputPhone || !isValidPhoneNumber(normalizedInputPhone)) {
          setError('Please enter a valid phone number');
          return;
        }
        changes.contactInformation = {
          ...user?.contactInformation,
          mobilePhone: normalizedInputPhone,
        };
        hasChanges = true;
        // console.log('Phone changed:', { old: user?.contactInformation?.mobilePhone, new: normalizedInputPhone });
      }
    }
    
    const newStoreLocationId = formData.storeLocationId ? parseInt(formData.storeLocationId) : undefined;
    if (newStoreLocationId !== user?.storeLocationId) {
      changes.storeLocationId = newStoreLocationId;
      hasChanges = true;
    }

    // Compare against the EFFECTIVE preference: a first choice of 'delivery'
    // must still be sent even though 'delivery' is already the stored default.
    // The server records when it was chosen.
    if (
      formData.deliveryPreference &&
      formData.deliveryPreference !== effectiveDeliveryPreference(user)
    ) {
      changes.deliveryPreference = formData.deliveryPreference;
      hasChanges = true;
    }

    // Check address changes.
    //
    // This used to send a single-element array built from the first address,
    // which silently destroyed every other address on the account — including
    // ones an admin had added. The whole book goes up now, ids and all.
    const nextAddresses = toSavePayload(formData.addresses);
    const currentAddresses = toSavePayload(user?.addresses ?? []);

    if (JSON.stringify(nextAddresses) !== JSON.stringify(currentAddresses)) {
      changes.addresses = nextAddresses;
      hasChanges = true;
    }

    // Check additional profile fields
    if (formData.gender !== (user?.gender || '')) {
      changes.gender = formData.gender;
      hasChanges = true;
    }

    // Normalize user's dateOfBirth for comparison
    let userDateOfBirth = '';
    if (user?.dateOfBirth) {
      try {
        const dateStr = user.dateOfBirth;
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          userDateOfBirth = dateStr;
        } else {
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            userDateOfBirth = date.toISOString().split('T')[0];
          }
        }
      } catch (e) {
        console.error('Error parsing user dateOfBirth:', e);
      }
    }

    if (formData.dateOfBirth && formData.dateOfBirth !== userDateOfBirth) {
      // Convert YYYY-MM-DD to ISO datetime string for the API
      // Set time to midnight UTC
      const date = new Date(formData.dateOfBirth + 'T00:00:00.000Z');
      changes.dateOfBirth = date.toISOString();
      hasChanges = true;
    }

    if (formData.trnNumber !== (user?.trnNumber || '')) {
      changes.trnNumber = formData.trnNumber;
      hasChanges = true;
    }

    // If no changes, return early
    if (!hasChanges) {
      // console.log('No changes detected, skipping save');
      return;
    }

    // console.log('Saving profile changes:', changes);
    const apiData = changes;
    setIsLoading(true);
    setError(null);
    try {
      if (user?.id) {
        const courierUserApi = new CourierUserApi(apiConfig());
        
        // Update the user profile via API
        const updatedUser = await courierUserApi.courierUserPrototypePatchAttributes({
          id: user.id.toString(),
          data: apiData
        });

        // Update Redux store with the response, preserving existing fields
        dispatch(updateUser({
          ...user,
          ...updatedUser,
          contactInformation: {
            ...user?.contactInformation,
            ...updatedUser.contactInformation
          }
        }));
        // Field NAMES only — never the values (PII).
        track('profile_updated', { fields_changed: Object.keys(changes) });
      }
    } catch (err) {
      setError('Failed to update profile');
      console.error('Error updating profile:', err);
    } finally {
      setIsLoading(false);
    }
    },
    [dispatch, user, formData, track]
  );

  const handleChangePassword = useCallback(async () => {
    if (!currentPassword) {
      setError('Please enter your current password');
      return;
    }
    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const courierUserApi = new CourierUserApi(apiConfig());
      await courierUserApi.courierUserChangePassword({
        courierUserChangePasswordRequest: {
          oldPassword: currentPassword,
          newPassword: newPassword
        }
      });

      track('password_changed');
      setShowChangePasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError('Failed to change password');
      console.error('Error changing password:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPassword, newPassword, confirmPassword, track]);

  const handleDeleteAccount = useCallback(async () => {
    if (deleteConfirmation !== 'DELETE') {
      setError('Please type DELETE to confirm account deletion');
      return;
    }

    if (!user?.id) {
      setError('User ID not found');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const courierUserApi = new CourierUserApi(apiConfig());
      await courierUserApi.courierUserDeleteById({
        id: user.id.toString()
      });

      // Clear local storage and redux state
      localStorage.clear();
      dispatch(logout());

      // Redirect to home page
      window.location.href = '/';
    } catch (err) {
      setError('Failed to delete account');
      console.error('Error deleting account:', err);
    } finally {
      setIsLoading(false);
    }
  }, [deleteConfirmation, user?.id, dispatch]);

  return {
    user,
    formData,
    isLoading,
    error,
    storeLocations,
    handleFieldChange,
    handleAddressChange,
    handleAddAddress,
    handleRemoveAddress,
    handleSetPrimaryAddress,
    saveProfile,
    // Password change
    showChangePasswordModal,
    setShowChangePasswordModal,
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    showCurrentPassword,
    setShowCurrentPassword,
    showNewPassword,
    setShowNewPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    handleChangePassword,
    // Delete account
    showDeleteAccountModal,
    setShowDeleteAccountModal,
    deleteConfirmation,
    setDeleteConfirmation,
    handleDeleteAccount,
  };
};